import {
  QCVisitExtendedWithContextDataAndDates as Visit,
  sortVisitsByLockStatus,
} from 'src/pages/QC/hooks/useMergedVisits.hook';

const findVisitById = (visitId: string, visits: Visit[]) =>
  visits.find(visit => visit.id === visitId);
const findPrevVisit = (currentVisitOrder: number, visits: Visit[]) =>
  visits.find(visit => visit.customOrder === currentVisitOrder - 1);
const findNextVisit = (currentVisitOrder: number, visits: Visit[]) =>
  visits.find(visit => visit.customOrder === currentVisitOrder + 1);

const updateCurrentVisit = (
  visit: Visit,
  shiftVisit: Visit,
  isCurrentLocked: boolean,
  isShiftLocked: boolean,
  setIsQueueChanged: (val: boolean) => void
): Visit => {
  if (isShiftLocked) {
    setIsQueueChanged(true);
    return {
      ...visit,
      customOrder: isCurrentLocked ? shiftVisit.customOrder : visit.customOrder,
      isDirty: true,
      visitOrderLocked: true,
    };
  }

  setIsQueueChanged(false);
  if (isCurrentLocked) {
    return {
      ...visit,
      isDirty: false,
      visitOrderLocked: false,
    };
  }

  return {
    ...visit,
    customOrder: shiftVisit.customOrder,
  };
};

const updateShiftVisit = (
  visit: Visit,
  currentVisit: Visit,
  isCurrentLocked: boolean,
  isShiftLocked: boolean
): Visit => {
  if (isCurrentLocked === isShiftLocked) {
    return {
      ...visit,
      customOrder: currentVisit.customOrder,
    };
  }

  return visit;
};

export const handleShiftVisit = (
  currentVisitId: string | null,
  visits: Visit[],
  shiftOrderFunction: (
    visitOrder: number,
    visits: Visit[]
  ) => Visit | undefined,
  updateVisitOrder: (visits: Visit[]) => void,
  setIsQueueChanged: (val: boolean) => void
) => {
  if (!currentVisitId) return;

  const currentVisit = findVisitById(currentVisitId, visits);
  if (!currentVisit) return;

  const shiftVisit = shiftOrderFunction(currentVisit.customOrder ?? 0, visits);
  if (!shiftVisit) return;

  const isCurrentLocked = currentVisit.visitOrderLocked;
  const isShiftLocked = shiftVisit.visitOrderLocked;

  const updatedVisits = visits.map(visit => {
    if (visit.id === currentVisit.id) {
      return updateCurrentVisit(
        visit,
        shiftVisit,
        isCurrentLocked,
        isShiftLocked,
        setIsQueueChanged
      );
    }

    if (visit.id === shiftVisit.id) {
      return updateShiftVisit(
        visit,
        currentVisit,
        isCurrentLocked,
        isShiftLocked
      );
    }

    return visit;
  });

  const sortedVisits = sortVisitsByLockStatus(updatedVisits);

  updateVisitOrder(sortedVisits);
};

export const moveVisitLeft = (
  currentVisitId: string | null,
  visits: Visit[],
  updateVisitOrder: (visits: Visit[]) => void,
  setIsQueueChanged: (val: boolean) => void
) =>
  handleShiftVisit(
    currentVisitId,
    visits,
    findPrevVisit,
    updateVisitOrder,
    setIsQueueChanged
  );

export const moveVisitRight = (
  currentVisitId: string | null,
  visits: Visit[],
  updateVisitOrder: (visits: Visit[]) => void,
  setIsQueueChanged: (val: boolean) => void
) =>
  handleShiftVisit(
    currentVisitId,
    visits,
    findNextVisit,
    updateVisitOrder,
    setIsQueueChanged
  );
